import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';

export function Verification() {
  const { applications, applicationAction } = useApp();
  return (
    <div data-testid="admin-verification">
      <h2 className="mb-4 text-base font-semibold text-kelate-ink">Merchant Verification</h2>
      <div className="space-y-3">
        {applications.map((a) => (
          <div key={a.id} data-testid={`application-${a.id}`} className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-kelate-ink">{a.businessName} <span className="text-xs font-normal text-kelate-soft">· {a.category} · {a.district}</span></span>
              <StatusBadge status={a.status} />
            </div>
            <div className="mt-2 grid gap-1 text-xs text-kelate-soft sm:grid-cols-2">
              <span>Phone: {a.phone} · Email: {a.email}</span>
              <span>Plan applied: <b className="text-kelate-ink">{a.plan}</b></span>
              <span>Bank: {a.bank.bank} · <span className="font-mono">{a.bank.masked}</span></span>
              <span>Submitted {timeAgo(a.submittedAt)}</span>
            </div>
            <div className="mt-2 rounded-sm bg-secondary/50 px-3 py-2 text-[11px] text-kelate-soft">
              Checklist: Business info ✓ · Phone reachable ✓ · Bank details ✓ · Documents pending review
            </div>
            {a.status === 'PENDING' && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`approve-${a.id}`} onClick={() => applicationAction(a.id, 'approve')} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Approve</button>
                <button data-testid={`changes-${a.id}`} onClick={() => applicationAction(a.id, 'changes')} className="rounded-sm border border-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-darkwood">Request Changes</button>
                <button data-testid={`reject-${a.id}`} onClick={() => applicationAction(a.id, 'reject')} className="rounded-sm border border-kelate-red/40 px-3 py-1.5 text-xs font-medium text-kelate-red">Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MerchantsAdmin() {
  const { orders, adminMerchants } = useApp();
  const list = adminMerchants;
  return (
    <div data-testid="admin-merchants" className="space-y-3">
      <h2 className="text-base font-semibold text-kelate-ink">Merchants</h2>
      {list.map((m) => {
        const mOrders = orders.filter((o) => o.merchantId === m.id);
        const gmv = mOrders.reduce((s, o) => s + o.itemSubtotal, 0);
        return (
          <div key={m.id} data-testid={`admin-merchant-${m.slug}`} className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
            <div className="flex items-center gap-3">
              {m.logo ? (
                <img src={m.logo} alt="" className="h-10 w-10 rounded-xl border border-kelate-line object-cover" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-kelate-darkwood font-display text-sm font-semibold text-kelate-surface">{m.name[0]}</span>
              )}
              <div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-kelate-ink">{m.name} {m.verified && <BadgeCheck size={13} className="text-kelate-sage" />}</div>
                <div className="text-xs text-kelate-soft">{m.category} · {m.district} · {PLANS[m.plan].commission * 100}% commission</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-kelate-soft">
              <span>GMV <b className="text-kelate-darkwood">{fmtRM2(gmv)}</b></span>
              <span>Orders <b className="text-kelate-darkwood">{mOrders.length}</b></span>
              <span>Health <b className="text-kelate-sage">Good</b></span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Subscriptions() {
  const { adminMerchants, planRequests, planRequestAction } = useApp();
  const all = adminMerchants;
  const counts = { LIST: 0, STORE: 0, BOOST: 0, BRAND: 0 };
  all.forEach((m) => { counts[m.plan] += 1; });
  const mrr = all.reduce((s, m) => s + PLANS[m.plan].monthly, 0);
  const pendingReqs = planRequests.filter((r) => r.status === 'PENDING');
  return (
    <div data-testid="admin-subscriptions" className="space-y-6">
      {pendingReqs.length > 0 && (
        <section className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]" data-testid="upgrade-requests">
          <h3 className="mb-1 text-sm font-semibold text-kelate-ink">Upgrade Requests</h3>
          <p className="mb-3 text-xs text-kelate-soft">Verify the merchant's subscription payment, then approve to activate plan features.</p>
          <div className="space-y-2">
            {pendingReqs.map((r) => (
              <div key={r.id} data-testid={`upgrade-request-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-kelate-line/60 px-4 py-3 text-sm">
                <div>
                  <span className="font-medium text-kelate-ink">{r.merchantName}</span>
                  <span className="ml-2 text-xs text-kelate-soft">{r.fromPlan} → <b className="text-kelate-darkwood">{r.toPlan}</b> · {r.billing} · {timeAgo(r.createdAt)}</span>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`approve-upgrade-${r.id}`} onClick={() => planRequestAction(r.id, 'approve')} className="rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface">Verify & Approve</button>
                  <button data-testid={`reject-upgrade-${r.id}`} onClick={() => planRequestAction(r.id, 'reject')} className="rounded-sm border border-kelate-red/40 px-3 py-1.5 text-xs font-medium text-kelate-red">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="MRR" value={fmtRM2(mrr)} icon={Crown} testId="sub-mrr" />
        {Object.keys(counts).map((k) => <KpiCard key={k} label={k} value={counts[k]} testId={`sub-count-${k.toLowerCase()}`} />)}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Renewals (30d)" value="6" testId="sub-renewals" />
        <KpiCard label="Failed Payments" value="1" testId="sub-failed" />
        <KpiCard label="Upgrades (30d)" value="2" testId="sub-upgrades" />
        <KpiCard label="Churn" value="0%" testId="sub-churn" />
      </div>
    </div>
  );
}

